import { useEffect, useState } from "react";
import { api } from "@/api/client";
import type { OffsiteConfig } from "@/engine/app/offsite-config";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import { teamLabel } from "@/engine/app/team-label";
import { useStore } from "@/state/store";

/**
 * Zeigt die Leinwand in der Arbeitsphase die Themenfindung (Tag 1) statt
 * der normalen Arbeitsphase? Reine Funktion, damit Tag 2 prüfbar
 * unverändert bleibt — siehe `OffsiteConfig.themenfindung`.
 */
export function istThemenfindung(
  cfg: OffsiteConfig["themenfindung"],
  folie: number,
  jetzt: number = Date.now(),
): boolean {
  if (!cfg) return false;
  const bis = Date.parse(cfg.bis);
  if (!Number.isFinite(bis) || jetzt >= bis) return false;
  return folie < cfg.vorFolie;
}

interface TeamStatus {
  name: string | null;
  hatThema: boolean;
}

/**
 * Themenfindung auf der Leinwand: je Team nur „Thema steht" oder „noch
 * offen" — bewusst ohne den Text, den zeigt erst die Themen-Folie im Plenum.
 * Lädt wie Slide13bThemen (Teamliste + Steckbrief je Team) alle 4 s.
 */
export function ThemenfindungStatus() {
  const { festeTeams } = useOffsiteConfig();
  const { state } = useStore();
  const token = state.auth.token;
  const [teams, setTeams] = useState<Map<number, TeamStatus>>(new Map());

  useEffect(() => {
    let aktiv = true;
    const lade = async () => {
      try {
        const { teams: liste } = await api.teams.list(token);
        const eintraege = await Promise.all(
          liste.map(async (t) => {
            const r = await api.teams.getReq(token, t.number).catch(() => null);
            const thema = (r?.requirement?.fields?.thema ?? "").trim();
            return [t.number, { name: t.name, hatThema: thema.length > 0 }] as const;
          }),
        );
        if (aktiv) setTeams(new Map(eintraege));
      } catch {
        /* nächster Takt */
      }
    };
    void lade();
    const id = setInterval(lade, 4000);
    return () => {
      aktiv = false;
      clearInterval(id);
    };
  }, [token]);

  const nummern = festeTeams
    ? Array.from({ length: festeTeams }, (_, i) => i + 1)
    : [...teams.keys()].sort((a, b) => a - b);
  const fertig = nummern.filter((n) => teams.get(n)?.hatThema).length;

  return (
    <div className="themenfindung" data-testid="themenfindung-status">
      <div className="themenfindung-zaehler" data-testid="themenfindung-zaehler">
        <strong>{fertig}</strong> von {nummern.length} Teams haben ein Thema
      </div>
      <div className="themenfindung-grid">
        {nummern.map((n) => {
          const t = teams.get(n);
          const ok = !!t?.hatThema;
          return (
            <div
              key={n}
              className={`themenfindung-kachel${ok ? " is-fertig" : ""}`}
              data-testid={`themenfindung-team-${n}`}
            >
              <div className="themenfindung-label">{teamLabel(n)}</div>
              <div className="themenfindung-name">{t?.name || " "}</div>
              <div className="themenfindung-state">{ok ? "✓ Thema steht" : "noch offen"}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
